import * as React from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import {
  FaIcon,
  faXmark,
  faArrowDownToLine,
  faPlusSolid,
  faMinusSolid,
  faChevronLeft,
  faChevronRight,
  faPrint,
} from '@/lib/fa'
import { Button } from '@/ui/button'
import { Portal } from '@/ui/overlays/Portal'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { toast } from '@/lib/toasts/api'
import { usePrintPreview } from '@/modules/printouts/stores/printPreviewStore'
import {
  zipPreviewItems,
  downloadBlob,
} from '@/modules/printouts/utils/downloadPreviewItems'

// Ile stron poza widokiem trzymamy zamontowanych, zeby przewijanie nie migalo.
const OVERSCAN_PAGES = 3

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3]


const DOWNLOAD_COOLDOWN_MS = 1200

export const PrintPreviewModal: React.FC = () => {
  const { state, items, goToPreview, closePreview } = usePrintPreview()
  const { t } = useLocalize()
  const containerRef = React.useRef<HTMLDivElement>(null)
  const contentRef = React.useRef<HTMLDivElement>(null)

  const [zipping, setZipping] = React.useState(false)
  const [downloading, setDownloading] = React.useState<'single' | 'all' | null>(
    null,
  )

  const downloadLock = React.useRef(false)
  const cooldownRef = React.useRef<number | null>(null)

  const startDownload = (kind: 'single' | 'all') => {
    if (downloadLock.current) return false
    downloadLock.current = true
    setDownloading(kind)
    return true
  }

  const releaseDownloadAfterCooldown = () => {
    if (cooldownRef.current !== null) window.clearTimeout(cooldownRef.current)
    cooldownRef.current = window.setTimeout(() => {
      cooldownRef.current = null
      downloadLock.current = false
      setDownloading(null)
    }, DOWNLOAD_COOLDOWN_MS)
  }

  React.useEffect(
    () => () => {
      if (cooldownRef.current !== null) window.clearTimeout(cooldownRef.current)
    },
    [],
  )

  const downloadAll = async () => {
    if (!startDownload('all')) return
    setZipping(true)
    try {
      const archive = await zipPreviewItems(items)
      downloadBlob(
        new Blob([archive], { type: 'application/zip' }),
        t('printout.batch-zip-name'),
      )
    } catch (error) {
      console.error('Nie udalo sie spakowac dokumentow:', error)
      toast.error({ text: t('printout.generation-error') })
    } finally {
      setZipping(false)
      releaseDownloadAfterCooldown()
    }
  }

  const handleDownloadCurrent = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!startDownload('single')) {
      event.preventDefault()
      return
    }
    releaseDownloadAfterCooldown()
  }

  const isDownloading = downloading !== null

  const printFrameRef = React.useRef<HTMLIFrameElement>(null)
  const [printing, setPrinting] = React.useState(false)

  // Druk z ukrytego iframe z tym samym blobem - przegladarka otwiera wlasne okno
  // drukowania PDF, bez pobierania pliku.
  const handlePrint = () => {
    const frame = printFrameRef.current
    if (!frame || !state.blobUrl) return
    setPrinting(true)
    frame.onload = () => {
      try {
        frame.contentWindow?.focus()
        frame.contentWindow?.print()
      } catch (error) {
        console.error('Nie udalo sie wydrukowac dokumentu:', error)
        window.open(state.blobUrl!, '_blank')
      } finally {
        setPrinting(false)
      }
    }
    frame.src = state.blobUrl
  }

  React.useEffect(() => {
    const frame = printFrameRef.current
    if (frame) {
      frame.onload = null
      frame.removeAttribute('src')
    }
    setPrinting(false)
  }, [state.blobUrl])

  const [numPages, setNumPages] = React.useState<number | null>(null)
  const [naturalPageWidth, setNaturalPageWidth] = React.useState<number | null>(null)
  const [isLandscape, setIsLandscape] = React.useState(false)
  const [zoom, setZoom] = React.useState(2.5)
  const [defaultZoom, setDefaultZoom] = React.useState(2.5)
  const [currentPage, setCurrentPage] = React.useState(1)
  // Wirtualizacja: montujemy tylko strony w poblizu widoku. Wydruk calej firmy potrafi
  // miec 1500+ stron, a kazda <Page> to osobne zadanie renderowania w pdf.js - przy
  // renderowaniu wszystkich naraz przegladarka staje na kilkadziesiat sekund.
  // Pozostale strony zajmuja pusty div o zmierzonej wysokosci, wiec pasek przewijania,
  // numer biezacej strony i skok do strony dzialaja tak samo jak wczesniej.
  const [pageHeight, setPageHeight] = React.useState<number | null>(null)
  // Ref obok stanu: pomiar czytamy w petli scrollowania, ktora nie moze zalezec od
  // przerenderowania komponentu.
  const pageHeightRef = React.useRef<number | null>(null)
  const [visibleRange, setVisibleRange] = React.useState({ from: 0, to: 6 })
  const visibleRangeRef = React.useRef(visibleRange)
  visibleRangeRef.current = visibleRange
  const pageRefs = React.useRef<(HTMLDivElement | null)[]>([])

  const pageWidth = naturalPageWidth ? Math.round(naturalPageWidth * zoom) : undefined

  // Reset przy zamknieciu i przy przejsciu na kolejny dokument paczki.
  React.useEffect(() => {
    setNumPages(null)
    setNaturalPageWidth(null)
    setIsLandscape(false)
    setZoom(2.5)
    setDefaultZoom(2.5)
    setCurrentPage(1)
    pageRefs.current = []
  }, [state.blobUrl])

  // Once naturalPageWidth is known and landscape: compute fit-zoom from actual DOM width.
  // Runs after re-render so the modal has already switched to tw-w-[95vw].
  React.useEffect(() => {
    if (!naturalPageWidth || !isLandscape || !containerRef.current) return
    const available = containerRef.current.clientWidth - 32
    const fitZoom = [...ZOOM_STEPS].reverse().find((s) => naturalPageWidth * s <= available) ?? ZOOM_STEPS[0]
    setZoom(fitZoom)
    setDefaultZoom(fitZoom)
  }, [naturalPageWidth, isLandscape])


  React.useEffect(() => {
    const container = containerRef.current
    if (!container || !numPages) return

    let frame: number | null = null
    const sync = () => {
      frame = null
      const top = container.getBoundingClientRect().top
      let visible = 1
      pageRefs.current.forEach((el, i) => {
        if (!el || el.offsetHeight === 0) return
        if (el.getBoundingClientRect().top - top <= 24) visible = i + 1
      })
      setCurrentPage(visible)

      // Wysokosc placeholderow mierzymy z DOM zamontowanej strony. pdf.js oddaje
      // w `onLoadSuccess` wysokosc NIESKALOWANA (punkty PDF przy scale 1), a strony
      // renderuja sie na `pageWidth` - placeholdery dostawaly wiec inna wysokosc niz
      // realne strony i przewijanie skakalo przy kazdym montowaniu. Zmiana zoomu
      // przelicza wartosc, bo rozjezdza sie o wiecej niz pol piksela.
      for (let i = visibleRangeRef.current.from; i < visibleRangeRef.current.to; i++) {
        const el = pageRefs.current[i]
        if (!el || el.offsetHeight === 0) continue
        const measured = el.offsetHeight + 16
        if (
          pageHeightRef.current == null ||
          Math.abs(pageHeightRef.current - measured) > 0.5
        ) {
          pageHeightRef.current = measured
          setPageHeight(measured)
        }
        break
      }

      // Zakres do zamontowania liczymy z pozycji przewijania i zmierzonej wysokosci
      // strony - nie z refow, bo strony poza zakresem sa placeholderami.
      const effectiveHeight = pageHeightRef.current
      if (effectiveHeight && numPages) {
        const first = Math.floor(container.scrollTop / effectiveHeight)
        const perScreen = Math.ceil(container.clientHeight / effectiveHeight) + 1
        const from = Math.max(0, first - OVERSCAN_PAGES)
        const to = Math.min(numPages, first + perScreen + OVERSCAN_PAGES)
        setVisibleRange((prev) =>
          prev.from === from && prev.to === to ? prev : { from, to },
        )
      }
    }
    const schedule = () => {
      if (frame === null) frame = window.requestAnimationFrame(sync)
    }

    container.addEventListener('scroll', schedule, { passive: true })
    const observer = new ResizeObserver(schedule)
    if (contentRef.current) observer.observe(contentRef.current)
    sync()
    return () => {
      container.removeEventListener('scroll', schedule)
      observer.disconnect()
      if (frame !== null) window.cancelAnimationFrame(frame)
    }
  }, [numPages, state.blobUrl, pageWidth, pageHeight])

  const goToPage = (page: number) => {
    const container = containerRef.current
    const el = pageRefs.current[page - 1]
    if (!container || !el) return
    const offset =
      el.getBoundingClientRect().top - container.getBoundingClientRect().top
    container.scrollTo({ top: container.scrollTop + offset - 16, behavior: 'smooth' })
    setCurrentPage(page)
  }

  if (!state.isOpen || !state.blobUrl) return null

  const zoomIn = () => setZoom((z) => ZOOM_STEPS.find((s) => s > z) ?? z)
  const zoomOut = () => setZoom((z) => [...ZOOM_STEPS].reverse().find((s) => s < z) ?? z)

  return (
    <Portal layer="modal" >
      <div className="tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-bg-[#1f2937] tw-bg-opacity-70">
        <div className={`tw-flex tw-h-auto tw-max-h-[85vh] tw-flex-col tw-overflow-hidden tw-rounded-lg tw-bg-white ${isLandscape ? 'tw-w-[95vw]' : 'tw-w-fit tw-min-w-[800px] tw-max-w-[95vw]'}`}>

          {/* Header */}
          <div className="tw-flex tw-h-[64px] tw-shrink-0 tw-items-center tw-justify-between tw-border-b tw-border-[#e5e6e6] tw-px-6">
            <h2 className="tw-truncate tw-text-xl tw-font-semibold tw-leading-[28px] tw-text-[#1f2937]">
              {state.filename || t('printout.preview')}
            </h2>
            <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-3">
            
              {state.count > 1 && (
                <div className="tw-flex tw-items-center tw-gap-1 tw-rounded tw-border tw-border-[#e5e6e6] tw-px-1">
                  <span className="tw-pl-1.5 tw-text-xs tw-font-medium tw-text-gray-500">
                    {t('printout.printout-label')}
                  </span>
                  <button
                    onClick={() => goToPreview(state.index - 1)}
                    disabled={state.index === 0}
                    className="tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded hover:tw-bg-gray-100 disabled:tw-opacity-30"
                    aria-label={t('common.previous')}
                  >
                    <FaIcon icon={faChevronLeft} className="tw-h-3 tw-w-3 tw-text-[#1f2937]" />
                  </button>
                  <span className="tw-min-w-[62px] tw-text-center tw-text-xs tw-font-medium tw-text-[#1f2937]">
                    {t('printout.document-position')
                      .replace('{0}', String(state.index + 1))
                      .replace('{1}', String(state.count))}
                  </span>
                  <button
                    onClick={() => goToPreview(state.index + 1)}
                    disabled={state.index === state.count - 1}
                    className="tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded hover:tw-bg-gray-100 disabled:tw-opacity-30"
                    aria-label={t('common.next')}
                  >
                    <FaIcon icon={faChevronRight} className="tw-h-3 tw-w-3 tw-text-[#1f2937]" />
                  </button>
                </div>
              )}
              {numPages != null && numPages > 1 && (
                <div className="tw-flex tw-items-center tw-gap-1 tw-rounded tw-border tw-border-[#e5e6e6] tw-px-1">
                  <span className="tw-pl-1.5 tw-text-xs tw-font-medium tw-text-gray-500">
                    {t('printout.page-label')}
                  </span>
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded hover:tw-bg-gray-100 disabled:tw-opacity-30"
                    aria-label={t('common.previous')}
                  >
                    <FaIcon icon={faChevronLeft} className="tw-h-3 tw-w-3 tw-text-[#1f2937]" />
                  </button>
                  <span className="tw-min-w-[62px] tw-text-center tw-text-xs tw-font-medium tw-text-[#1f2937]">
                    {currentPage} / {numPages}
                  </span>
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage >= numPages}
                    className="tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded hover:tw-bg-gray-100 disabled:tw-opacity-30"
                    aria-label={t('common.next')}
                  >
                    <FaIcon icon={faChevronRight} className="tw-h-3 tw-w-3 tw-text-[#1f2937]" />
                  </button>
                </div>
              )}
              <div className="tw-flex tw-items-center tw-gap-1 tw-rounded tw-border tw-border-[#e5e6e6] tw-px-1">
                <button
                  onClick={zoomOut}
                  disabled={zoom <= ZOOM_STEPS[0]}
                  className="tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded hover:tw-bg-gray-100 disabled:tw-opacity-30"
                  aria-label="Zoom out"
                >
                  <FaIcon icon={faMinusSolid} className="tw-h-3 tw-w-3 tw-text-[#1f2937]" />
                </button>
                <button
                  onClick={() => setZoom(defaultZoom)}
                  className="tw-min-w-[46px] tw-rounded tw-px-1 tw-py-0.5 tw-text-center tw-text-xs tw-font-medium tw-text-[#1f2937] hover:tw-bg-gray-100"
                  title="Reset zoom"
                >
                  {Math.round(zoom * 100)}%
                </button>
                <button
                  onClick={zoomIn}
                  disabled={zoom >= ZOOM_STEPS[ZOOM_STEPS.length - 1]}
                  className="tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded hover:tw-bg-gray-100 disabled:tw-opacity-30"
                  aria-label="Zoom in"
                >
                  <FaIcon icon={faPlusSolid} className="tw-h-3 tw-w-3 tw-text-[#1f2937]" />
                </button>
              </div>
              <button
                onClick={closePreview}
                disabled={zipping}
                className="tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded hover:tw-bg-gray-100 disabled:tw-opacity-30"
                aria-label="Close"
              >
                <FaIcon icon={faXmark} className="tw-h-5 tw-w-5 tw-text-[#1f2937]" />
              </button>
            </div>
          </div>

          {/* PDF content */}
          <div ref={containerRef} className="tw-flex-1 tw-overflow-auto tw-bg-[#f5f5f5] tw-p-4">
            <div ref={contentRef} className="tw-flex tw-min-w-fit tw-flex-col tw-items-center">
            <Document
              file={state.blobUrl}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              loading={
                <div className="tw-flex tw-h-40 tw-items-center tw-justify-center tw-text-sm tw-text-gray-500">
                  {t('printout.loading-preview')}
                </div>
              }
              error={
                <div className="tw-flex tw-h-40 tw-items-center tw-justify-center tw-text-sm tw-text-red-500">
                  {t('printout.preview-error')}
                </div>
              }
            >
              {numPages != null &&
                Array.from({ length: numPages }, (_, i) => {
                  const mounted = i >= visibleRange.from && i < visibleRange.to
                  return (
                    <div
                      key={i}
                      ref={(el) => {
                        pageRefs.current[i] = el
                      }}
                      className="tw-mb-4 tw-shadow-md"
                      style={
                        mounted || !pageHeight
                          ? undefined
                          : { height: pageHeight }
                      }
                    >
                      {mounted && (
                        <Page
                          pageNumber={i + 1}
                          width={pageWidth}
                          renderTextLayer
                          renderAnnotationLayer
                          onLoadSuccess={(page) => {
                            if (i === 0) {
                              const vp = page.getViewport({ scale: 1 })
                              setIsLandscape(vp.width > vp.height)
                              setNaturalPageWidth(vp.width)
                            }
                          }}
                        />
                      )}
                    </div>
                  )
                })}
            </Document>
            </div>
          </div>

          {/* Footer */}
          <div className="tw-flex tw-h-[70px] tw-shrink-0 tw-items-center tw-justify-between tw-border-t tw-border-[#e5e6e6] tw-bg-white tw-px-6">
            <span className="tw-text-xs tw-text-gray-400">
              {numPages != null ? `${numPages} ${t('printout.pages')}` : ''}
            </span>
            <div className="tw-flex tw-items-center tw-gap-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={closePreview}
                disabled={zipping}
              >
                {t('common.close')}
              </Button>
              {state.count > 1 && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={downloadAll}
                  loading={downloading === 'all'}
                  disabled={isDownloading}
                >
                  <FaIcon icon={faArrowDownToLine} className="tw-mr-2 tw-h-3.5 tw-w-3.5" />
                  {(zipping
                    ? t('printout.batch-zip-preparing')
                    : t('printout.download-all')
                  ).replace('{0}', String(state.count))}
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={handlePrint}
                loading={printing}
                disabled={printing || numPages == null}
              >
                <FaIcon icon={faPrint} className="tw-mr-2 tw-h-3.5 tw-w-3.5" />
                {t('common.print')}
              </Button>
              <a
                href={state.blobUrl}
                download={state.filename}
                onClick={handleDownloadCurrent}
                className={isDownloading ? 'tw-pointer-events-none' : undefined}
                tabIndex={isDownloading ? -1 : undefined}
              >
                <Button
                  variant="primary"
                  size="sm"
                  loading={downloading === 'single'}
                  disabled={isDownloading}
                >
                  <FaIcon icon={faArrowDownToLine} className="tw-mr-2 tw-h-3.5 tw-w-3.5" />
                  {t('common.download')}
                </Button>
              </a>
            </div>
          </div>

        </div>
        <iframe
          ref={printFrameRef}
          title="print"
          aria-hidden
          tabIndex={-1}
          className="tw-pointer-events-none tw-fixed tw-bottom-0 tw-right-0 tw-h-0 tw-w-0 tw-border-0 tw-opacity-0"
        />
      </div>
    </Portal>
  )
}
